import React from 'react'

/**
 * InvoiceSummary — left-panel of Step 3. 1:1 port of Scale Portal's
 * scale-portal/src/components/template/HourlyInvoice/InvoiceSummary.jsx.
 *
 * Layout sections (in order):
 *   1. Outer card (max-w-3xl p-5 bg-white rounded-lg border)
 *   2. RECIPIENT DETAILS — gray-tinted box, 2-col grid per party (label left,
 *      details right-aligned), HR between plaintiff and defendant sides
 *   3. HR
 *   4. INVOICE DETAILS — gray-tinted box: two date cards side-by-side, then
 *      TOTAL AMOUNT card with teal 2.5px border + USD badge
 *   5. LINE ITEMS — bordered container; each item shows description on top,
 *      then HH:MM hrs × $rate/hr LEFT, amount RIGHT
 *   6. NOTES (only if values.notes)
 *   7. Admin fee box — green-50 if collected, amber-50 otherwise; header row
 *      with "Scale Mediation Admin Fee" + amount + "per party"; descriptive
 *      paragraph below
 */

function fmtMoney(n) {
    const v = Number(n) || 0
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(s) {
    if (!s) return '—'
    try {
        const m = typeof s === 'string' && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10).split('-') : null
        const d = m ? new Date(Number(m[0]), Number(m[1]) - 1, Number(m[2])) : new Date(s)
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch (_e) { return s }
}

function decimalToHHMM(decimal) {
    const v = Number(decimal) || 0
    const h = Math.floor(v)
    const m = Math.round((v - h) * 60)
    return `${h}:${String(m).padStart(2, '0')}`
}

const PencilIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 122.88 121.51" width="15" height="15" fill="currentColor">
        <path d="M28.66,1.64H58.88L44.46,16.71H28.66a13.52,13.52,0,0,0-9.59,4l0,0a13.52,13.52,0,0,0-4,9.59v76.14H91.21a13.5,13.5,0,0,0,9.59-4l0,0a13.5,13.5,0,0,0,4-9.59V77.3l15.07-15.74V92.85a28.6,28.6,0,0,1-8.41,20.22l0,.05a28.58,28.58,0,0,1-20.2,8.39H11.5a11.47,11.47,0,0,1-8.1-3.37l0,0A11.52,11.52,0,0,1,0,110V30.3A28.58,28.58,0,0,1,8.41,10.09L8.46,10a28.58,28.58,0,0,1,20.2-8.4ZM73,76.47l-29.42,6,4.25-31.31L73,76.47ZM57.13,41.68,96.3.91A2.74,2.74,0,0,1,99.69.38l22.48,21.76a2.39,2.39,0,0,1-.19,3.57L82.28,67,57.13,41.68Z"/>
    </svg>
)

function fmtPhone(phone) {
    if (!phone) return ''
    const cleaned = String(phone).replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned[0] === '1') {
        const m = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/)
        if (m) return `+1 ${m[1]}-${m[2]}-${m[3]}`
    } else if (cleaned.length === 10) {
        const m = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
        if (m) return `+1 ${m[1]}-${m[2]}-${m[3]}`
    }
    return phone
}

// Single party block: label LEFT, details right-aligned.
// CC: section appears when invoice_emails has > 1 entry (Scale parity).
// `na=true` → render 'N/A' for any missing field (Scale's "one" mode behavior).
// `na=false` → omit empty fields (Scale's "all" mode renders empty <p>; we
//              just hide them for cleaner output, matching what users see when
//              fields aren't populated).
function PartyBlock({ label, name, email, phone, address, invoice_emails, na = false }) {
    const ccList = (() => {
        if (!invoice_emails) return []
        const list = Array.isArray(invoice_emails)
            ? invoice_emails
            : String(invoice_emails).split(',').map((s) => s.trim()).filter(Boolean)
        return list.length > 1 ? list : []
    })()
    // Match Scale: CC label is always font-extrabold regardless of na.
    const ccBoldClass = 'font-extrabold'
    return (
        <div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <p className="font-semibold">{label}</p>
                </div>
                <div className="space-y-1 text-right">
                    <p className="font-semibold capitalize">{name || (na ? 'N/A' : '—')}</p>
                    {na ? (
                        <>
                            <p>{email || 'N/A'}</p>
                            <p>{phone ? fmtPhone(phone) : 'N/A'}</p>
                            <p>{address || 'N/A'}</p>
                        </>
                    ) : (
                        <>
                            {email ? <p className="break-all">{email}</p> : null}
                            {phone ? <p>{fmtPhone(phone)}</p> : null}
                            {address ? <p className="break-words">{address}</p> : null}
                        </>
                    )}
                </div>
            </div>
            {ccList.length > 0 && (
                <div className="grid grid-cols-12">
                    <div className="col-span-6"></div>
                    <div className="col-span-6 flex flex-col items-end">
                        <p className={`text-[13px] ${ccBoldClass} text-gray-500 whitespace-nowrap self-start`}>CC:</p>
                        <div className="text-sm font-normal space-y-1 w-full text-right">
                            {ccList.map((em, i) => (
                                <p key={`cc-${i}`} title={em} className="truncate text-gray-500">{em}</p>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default function InvoiceSummary({
    values,
    onEditRecipient,
    onEditDetails,
    plaintiff,
    defendant,
    additionalPlaintiffParties = [],
    additionalDefendantParties = [],
    selectedRecipient,
    isAll,
    adminFeeAmount,
    adminFeeCollected,
    isAdminFeeAddedInLineItem,
}) {
    const items = values.items || []

    // Lock the card width to Scale's 480px (DevTools measured 480 x 787.5).
    // The wider page wrapper (1600px max) was making this fill the entire
    // col-span-4 cell. max-w-full keeps it from overflowing on small viewports.
    return (
        <div className="w-[480px] max-w-full p-5 bg-white rounded-lg border">
            {/* Empty filler — Scale has this at the top of the card. */}
            <div className="flex justify-between items-center"></div>
            {/* Section: RECIPIENT DETAILS */}
            <section className="mb-8">
                <div className="flex flex-col gap-5 bg-[#F9FAFBCC] rounded-md p-4 py-5 max-h-[415px] overflow-y-auto">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <p className="font-semibold">RECIPIENT DETAILS</p>
                            <button
                                type="button"
                                onClick={onEditRecipient}
                                className="text-teal-800 hover:text-teal-600 transition-colors"
                                title="Edit"
                            >
                                <PencilIcon />
                            </button>
                        </div>
                    </div>

                    {isAll ? (
                        <div className="space-y-4">
                            {plaintiff && (
                                <PartyBlock
                                    label="Plaintiff"
                                    name={plaintiff.name}
                                    email={plaintiff.email}
                                    phone={plaintiff.phone || plaintiff.phone_number}
                                    address={plaintiff.address}
                                    invoice_emails={plaintiff.invoice_emails}
                                />
                            )}
                            {additionalPlaintiffParties.map((p, i) => (
                                <PartyBlock
                                    key={`pp-${p.id || i}`}
                                    label={`Plaintiff Party ${i + 2}`}
                                    name={p.name}
                                    email={p.email}
                                    phone={p.phone || p.phone_number}
                                    address={p.address}
                                    invoice_emails={p.invoice_emails}
                                />
                            ))}

                            <hr className="border border-gray-200 -mx-4" />

                            {defendant && (
                                <PartyBlock
                                    label="Defendant"
                                    name={defendant.name}
                                    email={defendant.email}
                                    phone={defendant.phone || defendant.phone_number}
                                    address={defendant.address}
                                    invoice_emails={defendant.invoice_emails}
                                />
                            )}
                            {additionalDefendantParties.map((p, i) => (
                                <PartyBlock
                                    key={`dp-${p.id || i}`}
                                    label={`Defendant Party ${i + 2}`}
                                    name={p.name}
                                    email={p.email}
                                    phone={p.phone || p.phone_number}
                                    address={p.address}
                                    invoice_emails={p.invoice_emails}
                                />
                            ))}
                        </div>
                    ) : (
                        <PartyBlock
                            label={selectedRecipient?.role
                                ? selectedRecipient.role.charAt(0).toUpperCase() + selectedRecipient.role.slice(1)
                                : 'Recipient'}
                            name={selectedRecipient?.name}
                            email={selectedRecipient?.email}
                            phone={selectedRecipient?.phone || selectedRecipient?.phone_number}
                            address={selectedRecipient?.address}
                            invoice_emails={selectedRecipient?.invoice_emails}
                            na
                        />
                    )}
                </div>
            </section>

            <hr className="my-6 border-gray-200" />

            {/* Section: INVOICE DETAILS */}
            <div className="bg-[#F9FAFBCC] rounded-md p-3 mb-4">
                <div className="flex items-center gap-2">
                    <p className="font-semibold">INVOICE DETAILS</p>
                    <button
                        type="button"
                        onClick={onEditDetails}
                        className="text-teal-800 hover:text-teal-600 transition-colors"
                        title="Edit"
                    >
                        <PencilIcon />
                    </button>
                </div>

                <div className="flex gap-3 py-2">
                    <div className="bg-white rounded-md w-1/2 p-3 space-y-2">
                        <p className="font-medium text-[13px]">ISSUE DATE</p>
                        <p className="text-[#17191C]">{fmtDate(values.date || new Date().toISOString())}</p>
                    </div>
                    <div className="bg-white rounded-md w-1/2 p-3 space-y-2">
                        <p className="font-medium text-[13px]">DUE DATE</p>
                        <p className="text-[#17191C]">{fmtDate(values.due_date)}</p>
                    </div>
                </div>

                <div className="flex mb-6 bg-white border-[2.5px] rounded-md justify-between items-center p-4" style={{ borderColor: '#027776' }}>
                    <div>
                        <p className="font-medium mb-2 text-[13px]">TOTAL AMOUNT</p>
                        <p className="text-base font-semibold text-[#17191C]">${fmtMoney(values.subtotal)}</p>
                    </div>
                    <div>
                        <span className="bg-gray-200 p-[5px] text-[10px] rounded-full">USD</span>
                    </div>
                </div>
            </div>

            {/* Section: LINE ITEMS */}
            <div className="mb-6">
                <div className="flex justify-between">
                    <h3 className="text-sm font-normal text-gray-700 mb-2">LINE ITEMS</h3>
                </div>
                <div className="space-y-4 border p-3 rounded-md">
                    {/* `_isCredit` marks the per-party share credit the hourly
                        EDIT wizard appends (previewAdapter.js). It is not hourly
                        work, so it gets no "hrs × $rate/hr" sub-line — that
                        formatting turns it into "1:00 hrs × $-600/hr". Gated on
                        the FLAG, not on `amount < 0`: this component is shared
                        with the create flow. */}
                    {items.map((item, i) => (
                        <div key={item.id || i} className="border-b border-gray-200 last:border-0">
                            <p className="font-medium text-gray-800 mb-1">{item.description || 'No description'}</p>
                            <div className="flex justify-between text-gray-600">
                                <span>
                                    {item._isCredit ? null : (
                                        <>{decimalToHHMM(item.quantity)} hrs × ${item.rate}/hr</>
                                    )}
                                </span>
                                <span className="font-semibold text-gray-900">
                                    {item._isCredit
                                        ? `- $${fmtMoney(Math.abs(Number(item.amount) || 0))}`
                                        : `$${fmtMoney(item.amount)}`}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Section: NOTES (only if present) — Scale uses `values.notes`,
                we historically stored it as `additional_notes`; accept either.
                No outer mb (Scale uses bare <div>). Inner content wrapped in
                extra <div> to match Scale's DOM. */}
            {(values.notes || values.additional_notes) ? (
                <div>
                    <p className="font-semibold mb-2">NOTES</p>
                    <div className="bg-gray-100 rounded-md p-3">
                        <div>
                            <p className="font-medium text-[13px]">
                                {values.notes || values.additional_notes}
                            </p>
                        </div>
                    </div>
                </div>
            ) : null}

            <br />

            {/* Section: Admin fee box */}
            <div className={`rounded-lg p-3 border border-gray-200 ${adminFeeCollected ? 'bg-green-50' : 'bg-amber-50'}`}>
                {!adminFeeCollected && (
                    <div className="flex justify-between items-start mb-1">
                        <p className="font-semibold text-gray-800">Scale Mediation Admin Fee</p>
                        <p className="text-gray-700">
                            <span className="font-semibold">${Number(adminFeeAmount) || 0}</span>
                            <span className="text-gray-500 text-sm ml-1">per party</span>
                        </p>
                    </div>
                )}
                <p className="text-gray-500 text-xs leading-relaxed">
                    {adminFeeCollected
                        ? 'The Scale Mediation fee already collected.'
                        : `The Scale Mediation fee will be automatically ${
                              isAdminFeeAddedInLineItem ? 'added' : 'deducted'
                          } ${
                              isAdminFeeAddedInLineItem ? 'to' : 'from'
                          } the invoice once ${
                              isAdminFeeAddedInLineItem ? 'sent' : 'paid'
                          }.`}
                </p>
            </div>
        </div>
    )
}
